import React, { useEffect, useState } from 'react';

interface CouponSuccessPopperProps {
  show: boolean;
  amount: number;
  couponCode: string;
  onComplete?: () => void;
}

const CouponSuccessPopper: React.FC<CouponSuccessPopperProps> = ({ 
  show, 
  amount, 
  couponCode,
  onComplete 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (show) {
      setIsVisible(true);
      setIsAnimating(true);
      
      // Hide after animation completes
      const timer = setTimeout(() => {
        setIsAnimating(false);
        setTimeout(() => {
          setIsVisible(false);
          onComplete?.();
        }, 300);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [show, onComplete]);

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 pointer-events-none z-50 flex items-center justify-center">
      <div
        className={`
          bg-gradient-to-r from-green-500 to-emerald-600 text-white px-8 py-6 rounded-2xl shadow-2xl
          transform transition-all duration-500 ease-out
          ${isAnimating 
            ? 'scale-100 opacity-100 translate-y-0' 
            : 'scale-50 opacity-0 translate-y-10'
          }
        `}
      >
        <div className="flex items-center gap-4">
          {/* Animated checkmark */}
          <div className="relative">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
              <svg 
                className="w-10 h-10 text-white animate-bounce"
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={3} 
                  d="M5 13l4 4L19 7"
                  className={`${isAnimating ? 'animate-draw-check' : ''}`}
                />
              </svg>
            </div>
            {/* Sparkle effects */}
            <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-300 rounded-full animate-ping" />
            <div className="absolute -bottom-1 -left-1 w-3 h-3 bg-yellow-300 rounded-full animate-ping animation-delay-200" />
            <div className="absolute top-0 right-8 w-2 h-2 bg-yellow-300 rounded-full animate-ping animation-delay-400" />
          </div>
          
          <div>
            <h3 className="text-2xl font-bold mb-1">Coupon Applied!</h3>
            <p className="text-lg">
              <span className="font-semibold">{couponCode}</span> saved you{' '}
              <span className="text-yellow-300 font-bold text-xl">₹{amount.toLocaleString()}</span>
            </p>
          </div>
        </div>

        {/* Confetti particles */}
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className={`
                absolute w-2 h-2 rounded-full
                ${i % 2 === 0 ? 'bg-yellow-300' : 'bg-pink-300'}
                animate-confetti-${i + 1}
              `}
              style={{
                left: `${20 + i * 15}%`,
                animationDelay: `${i * 100}ms`
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default CouponSuccessPopper;